import React, { useState, useEffect, useRef, useCallback } from 'react';

interface Announcement {
  id: number;
  title: string;
  body: string | null;
  storylane_url: string | null;
  created_at: string;
}

const getToken = () => localStorage.getItem('token');

const FeatureAnnouncementModal: React.FC = () => {
  const [announcements, setAnnouncements] = useState<Announcement[]>([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchUnseen = async () => {
      try {
        const res = await fetch('/api/announcements/unseen', {
          headers: { 'Authorization': `Bearer ${getToken()}` }
        });
        if (res.ok) {
          const data = await res.json();
          setAnnouncements(data.announcements || []);
        }
      } catch (err) {
        console.error('Failed to fetch announcements:', err);
      } finally {
        setLoading(false);
      }
    };

    const timer = setTimeout(fetchUnseen, 1500);
    return () => clearTimeout(timer);
  }, []);

  const handleDismiss = async () => {
    const current = announcements[currentIndex];
    if (!current) return;

    try {
      await fetch(`/api/announcements/${current.id}/dismiss`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${getToken()}` }
      });
    } catch (err) {
      console.error('Failed to dismiss:', err);
    }

    if (currentIndex < announcements.length - 1) {
      setCurrentIndex(currentIndex + 1);
    } else {
      setAnnouncements([]);
    }
  };

  if (loading || announcements.length === 0) return null;

  const current = announcements[currentIndex];
  const isLast = currentIndex === announcements.length - 1;
  const total = announcements.length;
  // Detect whether the stored value is the full HTML embed snippet or just a
  // bare URL. Old announcements predate the textarea field and stored a URL.
  const storylaneIsEmbedHtml = !!current.storylane_url && current.storylane_url.includes('<');

  return (
    <div className="modal-backdrop" style={{ zIndex: 60 }}>
      <div className="modal-dialog max-w-2xl w-full mx-4 max-h-[85vh] flex flex-col">
        {/* Header */}
        <div className="px-6 py-4 border-b border-gray-200/80 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center shadow-sm">
              <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5.882V19.24a1.76 1.76 0 01-3.417.592l-2.147-6.15M18 13a3 3 0 100-6M5.436 13.683A4.001 4.001 0 017 6h1.832c4.1 0 7.625-1.234 9.168-3v14c-1.543-1.766-5.067-3-9.168-3H7a3.988 3.988 0 01-1.564-.317z" />
              </svg>
            </div>
            <div>
              <h2 className="text-lg font-semibold text-gray-900 tracking-tight">What's New</h2>
              {total > 1 && (
                <p className="text-xs text-gray-500">{currentIndex + 1} of {total} updates</p>
              )}
            </div>
          </div>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto p-6">
          <h3 className="text-xl font-bold text-gray-900 tracking-tight mb-3">{current.title}</h3>

          {current.body && (
            <div
              className="text-sm text-gray-700 leading-relaxed whitespace-pre-wrap mb-4"
              dangerouslySetInnerHTML={{
                __html: current.body.replace(
                  /(https?:\/\/[^\s<]+)/g,
                  '<a href="$1" target="_blank" rel="noopener noreferrer" class="text-blue-600 hover:underline">$1</a>'
                )
              }}
            />
          )}

          {current.storylane_url && (
            storylaneIsEmbedHtml ? (
              // Admin pasted the full embed snippet. Mirror TrainingDetail's
              // approach: parse the HTML manually and re-create <script> tags
              // so the browser executes them (React drops scripts when using
              // dangerouslySetInnerHTML). Storylane controls aspect ratio and
              // popup-vs-inline mode itself via its own embed code.
              <EmbedRenderer key={current.id} embedCode={current.storylane_url} />
            ) : (
              // Backwards-compat: old announcements stored just a URL. Wrap
              // it in a 16:9 iframe and hope for the best.
              <div className="mt-4 rounded-xl overflow-hidden border border-gray-200/80 shadow-card">
                <div className="sl-embed" style={{ position: 'relative', paddingBottom: 'calc(56.25% + 25px)', width: '100%', height: 0 }}>
                  <iframe
                    loading="lazy"
                    className="sl-demo"
                    src={current.storylane_url}
                    allow="fullscreen"
                    allowFullScreen
                    style={{
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      width: '100%',
                      height: '100%',
                      border: 'none',
                      borderRadius: '10px'
                    }}
                  />
                </div>
              </div>
            )
          )}

          <p className="text-[10px] text-gray-400 mt-4">
            {new Date(current.created_at).toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' })}
          </p>
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-gray-200/80 flex items-center justify-between bg-gray-50/50">
          {total > 1 && (
            <div className="flex gap-1.5">
              {announcements.map((_, i) => (
                <div
                  key={i}
                  className={`w-2 h-2 rounded-full transition-all duration-200 ${
                    i === currentIndex ? 'bg-blue-600 scale-110' : i < currentIndex ? 'bg-blue-300' : 'bg-gray-300'
                  }`}
                />
              ))}
            </div>
          )}
          {total <= 1 && <div />}
          <button onClick={handleDismiss} className="btn-primary">
            {isLast ? 'Got it' : `Next (${total - currentIndex - 1} more)`}
          </button>
        </div>
      </div>
    </div>
  );
};

// Renders a Storylane (or any third-party) embed snippet that includes a
// <script> tag, by parsing the snippet and re-creating script elements so
// the browser actually executes them. dangerouslySetInnerHTML alone won't
// run scripts, which breaks Storylane's popup-mode demos. Mirrors the
// pattern used in TrainingDetail.tsx.
export const EmbedRenderer: React.FC<{ embedCode: string }> = ({ embedCode }) => {
  const embedRef = useRef<HTMLDivElement | null>(null);

  const renderEmbed = useCallback((code: string) => {
    if (!embedRef.current) return;
    embedRef.current.innerHTML = '';

    const temp = document.createElement('div');
    temp.innerHTML = code;

    Array.from(temp.childNodes).forEach(node => {
      if (node.nodeName !== 'SCRIPT') {
        embedRef.current!.appendChild(node.cloneNode(true));
      }
    });

    temp.querySelectorAll('script').forEach(oldScript => {
      const newScript = document.createElement('script');
      Array.from(oldScript.attributes).forEach(attr => {
        newScript.setAttribute(attr.name, attr.value);
      });
      if (oldScript.textContent) {
        newScript.textContent = oldScript.textContent;
      }
      embedRef.current!.appendChild(newScript);
    });
  }, []);

  useEffect(() => {
    renderEmbed(embedCode);
    return () => {
      if (embedRef.current) embedRef.current.innerHTML = '';
    };
  }, [embedCode, renderEmbed]);

  return <div ref={embedRef} className="storylane-embed mt-4" />;
};

export default FeatureAnnouncementModal;
